import type {
  DomainClassSummary,
  DomainMethodIR,
  ManifestStateMachineInput,
  MetamodelGraphqlMutation,
  DomainStateMachineSummary,
  DomainTypeIR,
} from "@granular-software/metamodel-core";
import {
  defineMetamodelPackage,
  quoteTsStringLiteral,
  toPascalCase,
} from "@granular-software/metamodel-core";

function normalizeStateMachines(
  values: Array<Record<string, any>> | null | undefined,
): DomainStateMachineSummary[] {
  const parseJsonRecord = (value: unknown) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    if (typeof value !== "string" || !value.trim()) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  };
  const parseJsonValue = (value: unknown) => {
    if (value === null || typeof value === "undefined") return null;
    if (typeof value !== "string") return value;
    if (!value.trim()) return null;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };
  return (values || [])
    .map((machine) => {
      const states = (machine?.states || [])
        .map((state: Record<string, any>) => ({
          name: String(state?.name || ""),
          label: typeof state?.label === "string" ? state.label : null,
          description:
            typeof state?.description === "string" ? state.description : null,
          isFinal: Boolean(state?.is_final ?? state?.isFinal),
        }))
        .filter((state: { name: string }) => state.name.length > 0);

      const transitions = (machine?.transitions || [])
        .map((transition: Record<string, any>) => ({
          name: String(transition?.name || ""),
          from: String(transition?.from?.name || ""),
          to: String(transition?.to?.name || ""),
          label:
            typeof transition?.label === "string" ? transition.label : null,
          description:
            typeof transition?.description === "string"
              ? transition.description
              : null,
          action:
            parseJsonRecord(transition?.action) ||
            parseJsonRecord(transition?.action_json),
          assignee:
            parseJsonRecord(transition?.assignee) ||
            parseJsonRecord(transition?.assignee_json),
          requirements:
            parseJsonRecord(transition?.requirements) ||
            parseJsonRecord(transition?.requirements_json),
          permission:
            parseJsonValue(transition?.permission) ??
            parseJsonValue(transition?.permission_json),
          risk:
            transition?.risk === "low" ||
            transition?.risk === "medium" ||
            transition?.risk === "high"
              ? transition.risk
              : null,
          expectedOutcome:
            parseJsonValue(transition?.expectedOutcome) ??
            parseJsonValue(transition?.expected_outcome_json),
        }))
        .filter(
          (transition: { name: string; from: string; to: string }) =>
            transition.name.length > 0 &&
            transition.from.length > 0 &&
            transition.to.length > 0,
        );

      return {
        name: String(machine?.name || ""),
        entryState:
          typeof machine?.entry_state?.name === "string"
            ? machine.entry_state.name
            : null,
        finalStates: states
          .filter((state: { isFinal: boolean }) => state.isFinal)
          .map((state: { name: string }) => state.name),
        states,
        transitions,
      } satisfies DomainStateMachineSummary;
    })
    .filter((machine) => machine.name.length > 0);
}

function stateTypeName(className: string, machineName: string) {
  return `${toPascalCase(className)}${toPascalCase(machineName)}`;
}

function transitionTypeName(className: string, machineName: string) {
  return `${stateTypeName(className, machineName)}Transition`;
}

function pathTypeName(className: string, machineName: string) {
  return `${stateTypeName(className, machineName)}Path`;
}

function methodToken(value: string | null | undefined) {
  const token = String(value || "")
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return token || "state";
}

function transitionActionsForMachine(machine: DomainStateMachineSummary) {
  return Object.fromEntries(
    (machine.transitions || [])
      .filter((transition) => transition.action?.effect)
      .map((transition) => [transition.name, transition.action!]),
  );
}

function normalizeStateDefinitions(machine: ManifestStateMachineInput) {
  const finalStates = new Set(machine.finalStates || []);
  const states = new Map<
    string,
    {
      name: string;
      label?: string;
      description?: string;
      isFinal: boolean;
    }
  >();

  for (const rawState of machine.states || []) {
    if (typeof rawState === "string") {
      states.set(rawState, {
        name: rawState,
        isFinal: finalStates.has(rawState),
      });
      continue;
    }

    states.set(rawState.name, {
      name: rawState.name,
      label: rawState.label,
      description: rawState.description,
      isFinal: Boolean(rawState.isFinal) || finalStates.has(rawState.name),
    });
  }

  if (!states.has(machine.entryState)) {
    states.set(machine.entryState, {
      name: machine.entryState,
      isFinal: finalStates.has(machine.entryState),
    });
  }

  return [...states.values()];
}

function transitionMetadataGraphqlArgs(
  transition: ManifestStateMachineInput["transitions"][number],
) {
  const args: string[] = [];
  if (typeof transition.label === "string") {
    args.push(`label: ${JSON.stringify(transition.label)}`);
  }
  if (typeof transition.description === "string") {
    args.push(`description: ${JSON.stringify(transition.description)}`);
  }
  if (transition.action) {
    args.push(
      `action_json: ${JSON.stringify(JSON.stringify(transition.action))}`,
    );
  }
  if (transition.assignee) {
    args.push(
      `assignee_json: ${JSON.stringify(JSON.stringify(transition.assignee))}`,
    );
  }
  if (transition.requirements) {
    args.push(
      `requirements_json: ${JSON.stringify(JSON.stringify(transition.requirements))}`,
    );
  }
  if (transition.permission) {
    args.push(
      `permission_json: ${JSON.stringify(JSON.stringify(transition.permission))}`,
    );
  }
  if (transition.risk) {
    args.push(`risk: ${JSON.stringify(transition.risk)}`);
  }
  if (transition.expectedOutcome) {
    args.push(
      `expected_outcome_json: ${JSON.stringify(JSON.stringify(transition.expectedOutcome))}`,
    );
  }
  return args.length > 0 ? `, ${args.join(", ")}` : "";
}

export function buildStateMachineModelMutations(
  modelPath: string,
  machines?: ManifestStateMachineInput[],
): MetamodelGraphqlMutation[] {
  const mutations: MetamodelGraphqlMutation[] = [];

  for (const machine of machines || []) {
    mutations.push({
      label: `create state machine ${modelPath}.${machine.name}`,
      query: `mutation { at(path: ${JSON.stringify(modelPath)}) { create_state_machine(name: ${JSON.stringify(
        machine.name,
      )}, entry_state: ${JSON.stringify(machine.entryState)}) { name } } }`,
    });

    for (const state of normalizeStateDefinitions(machine)) {
      if (
        state.name === machine.entryState &&
        !state.isFinal &&
        !state.label &&
        !state.description
      )
        continue;
      mutations.push({
        label: `add state ${state.name} on ${modelPath}.${machine.name}`,
        query: `mutation { at(path: ${JSON.stringify(modelPath)}) { state_machine(name: ${JSON.stringify(
          machine.name,
        )}) { add_state(name: ${JSON.stringify(state.name)}, is_final: ${state.isFinal}, label: ${JSON.stringify(state.label || null)}, description: ${JSON.stringify(state.description || null)}) { name } } } }`,
      });
    }

    for (const transition of machine.transitions || []) {
      mutations.push({
        label: `add transition ${transition.name} on ${modelPath}.${machine.name}`,
        query: `mutation { at(path: ${JSON.stringify(modelPath)}) { state_machine(name: ${JSON.stringify(
          machine.name,
        )}) { add_transition(name: ${JSON.stringify(
          transition.name,
        )}, from: ${JSON.stringify(transition.from)}, to: ${JSON.stringify(
          transition.to,
        )}${transitionMetadataGraphqlArgs(transition)}) { name } } } }`,
      });
    }
  }

  return mutations;
}

function buildMachineTypes(
  classSummary: DomainClassSummary,
  machine: DomainStateMachineSummary,
): DomainTypeIR[] {
  const stateGlossary = machine.states.map((state) => {
    const label =
      state.label && state.label !== state.name ? state.label : null;
    const meaning = [label, state.description].filter(Boolean).join(" — ");
    const finalMarker = state.isFinal ? " Final state." : "";
    return `${state.name}${meaning ? `: ${meaning}` : "."}${finalMarker}`;
  });
  return [
    {
      kind: "union",
      name: stateTypeName(classSummary.name, machine.name),
      docs: [
        `Allowed states for ${classSummary.name}.${machine.name}.`,
        ...stateGlossary,
      ],
      members: machine.states.map((state) => state.name),
    },
    {
      kind: "union",
      name: transitionTypeName(classSummary.name, machine.name),
      docs: [`Allowed transitions for ${classSummary.name}.${machine.name}.`],
      members: machine.transitions.map((transition) => transition.name),
    },
  ];
}

function buildMachineMethods(
  classSummary: DomainClassSummary,
  machine: DomainStateMachineSummary,
): DomainMethodIR[] {
  const stateName = stateTypeName(classSummary.name, machine.name);
  const transitionName = transitionTypeName(classSummary.name, machine.name);
  const pathName = pathTypeName(classSummary.name, machine.name);

  const docsPrefix = `${classSummary.name}.${machine.name}`;

  const methods: DomainMethodIR[] = [
    {
      name: `get_${machine.name}`,
      docs: [`Get the current ${docsPrefix} state.`],
      static: false,
      params: [],
      returnType: `Promise<${stateName} | null>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "get_current",
      },
    },
    {
      name: `reach_${machine.name}`,
      docs: [
        `Reach a ${docsPrefix} state through the shortest allowed transition path.`,
      ],
      static: false,
      params: [{ name: "target", type: stateName }],
      returnType: `Promise<${toPascalCase(classSummary.name)}>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "reach",
      },
    },
    {
      name: `list_${machine.name}_transitions`,
      docs: [`List currently available transitions for ${docsPrefix}.`],
      static: false,
      params: [],
      returnType: `Promise<${transitionName}[]>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "list_transitions",
      },
    },
    {
      name: `list_${machine.name}_reachable_states`,
      docs: [`List reachable states for ${docsPrefix} from the current state.`],
      static: false,
      params: [],
      returnType: `Promise<${stateName}[]>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "list_reachable_states",
      },
    },
    {
      name: `${machine.name}_is_final`,
      docs: [`Return whether the current ${docsPrefix} state is final.`],
      static: false,
      params: [],
      returnType: "Promise<boolean>",
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "is_final",
      },
    },
    {
      name: `paths_to_${machine.name}`,
      docs: [
        `List shortest transition paths from the current ${docsPrefix} state to a target state.`,
      ],
      static: false,
      params: [{ name: "target", type: stateName }],
      returnType: `Promise<Array<{ states: ${stateName}[]; transitions: ${transitionName}[] }>>`,
      runtime: {
        kind: "state_machine",
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "paths_to",
      },
    },
  ];
  const creationMethods = (classSummary.methods || []).filter(
    (method) =>
      method.static === true &&
      Boolean(method.creates) &&
      method.creates?.className === classSummary.name &&
      typeof method.effectKey === "string" &&
      method.effectKey.length > 0,
  );

  for (const state of machine.states) {
    const stateNameValue =
      typeof state === "string" ? state : String(state?.name || "");
    if (!stateNameValue) continue;
    const token = methodToken(stateNameValue);
    methods.push(
      {
        name: `reach_${machine.name}_to_${token}`,
        docs: [`Reach ${docsPrefix} state ${stateNameValue}.`],
        static: false,
        params: [],
        returnType: `Promise<${toPascalCase(classSummary.name)}>`,
        runtime: {
          kind: "state_machine",
          machineName: machine.name,
          className: classSummary.name,
          stateTypeName: stateName,
          transitionTypeName: transitionName,
          operation: "reach",
          targetState: stateNameValue,
          transitionActions: transitionActionsForMachine(machine),
        },
      },
      {
        name: `prepare_${machine.name}_to_${token}`,
        docs: [
          `Prepare a reviewable artifact that can move ${docsPrefix} to ${stateNameValue}.`,
        ],
        static: false,
        params: [],
        returnType: "Promise<SessionArtifactRecord>",
        runtime: {
          kind: "state_machine",
          machineName: machine.name,
          className: classSummary.name,
          stateTypeName: stateName,
          transitionTypeName: transitionName,
          operation: "prepare_reach",
          targetState: stateNameValue,
          transitionActions: transitionActionsForMachine(machine),
        },
      },
    );

    for (const creationMethod of creationMethods) {
      const creationRuntime = {
        kind: "state_machine" as const,
        machineName: machine.name,
        className: classSummary.name,
        stateTypeName: stateName,
        transitionTypeName: transitionName,
        operation: "prepare_create_reach" as const,
        targetState: stateNameValue,
        transitionActions: transitionActionsForMachine(machine),
        creation: {
          methodName: creationMethod.name,
          effectKey: creationMethod.effectKey || creationMethod.name,
          inputSchema: creationMethod.inputSchema,
          outputSchema: creationMethod.outputSchema,
          creates: creationMethod.creates!,
        },
      };
      const viaName = `prepare_${machine.name}_to_${token}_via_${methodToken(creationMethod.name)}`;
      methods.push({
        name: viaName,
        docs: [
          `Prepare a reviewable artifact that will create a new ${classSummary.name} through ${creationMethod.name}, then move ${docsPrefix} to ${stateNameValue}.`,
        ],
        static: true,
        params: [
          { name: "input", type: "Record<string, any>", optional: true },
        ],
        returnType: "Promise<SessionArtifactRecord>",
        runtime: creationRuntime,
      });

      if (creationMethods.length === 1) {
        methods.push({
          name: `prepare_${machine.name}_to_${token}`,
          docs: [
            `Prepare a reviewable artifact that will create a new ${classSummary.name}, then move ${docsPrefix} to ${stateNameValue}.`,
          ],
          static: true,
          params: [
            { name: "input", type: "Record<string, any>", optional: true },
          ],
          returnType: "Promise<SessionArtifactRecord>",
          runtime: creationRuntime,
        });
      }
    }
  }

  return methods;
}

export function readStateMachineSummaries(
  rawClass: Record<string, any>,
): Partial<DomainClassSummary> {
  return {
    stateMachines: normalizeStateMachines(rawClass.states),
  };
}

export const stateMachineMetamodelPackage = defineMetamodelPackage({
  id: "state_machine",
  docs: {
    modelRows: [
      {
        key: "stateMachines",
        description:
          "State machine definitions with `entryState`, `states`, and `transitions`.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type StateMachineMutation {
        name: String!
        state_machine: StateMachine!
        add_state(name: String!, is_final: Boolean, label: String, description: String): StateMachineMutation!
        add_transition(name: String!, from: String!, to: String!, label: String, description: String, action_json: String, assignee_json: String, requirements_json: String, permission_json: String, risk: String, expected_outcome_json: String): StateMachineMutation!
        activate_transition(name: String!): StateMachineMutation!
      }

      type StateMachineSnapshot {
        name: String!
        current_state: StateMachineState
        available_transitions: [StateMachineTransition!]!
        reachable_states: [StateMachineState!]!
        paths_to(state: String!): [StateMachinePath!]!
        is_final: Boolean!
        history: [StateMachineTransitionEvent!]!
      }

      type StateMachineSnapshotMutation {
        snapshot: StateMachineSnapshot!
        activate_transition(name: String!): StateMachineSnapshotMutation!
        observe_state(state: String!, force: Boolean, source: String): StateMachineSnapshotMutation!
      }

      type StateMachine {
        name: String!
        model: Model!
        entry_state: StateMachineState
        states: [StateMachineState!]!
        transitions: [StateMachineTransition!]!
        current_state: StateMachineState
        available_transitions: [StateMachineTransition!]!
        reachable_states: [StateMachineState!]!
        paths_to(state: String!): [StateMachinePath!]!
        is_final: Boolean!
        history: [StateMachineTransitionEvent!]!
        instances_in_state(state: String!): [Model!]!
        instance_population(state: String!, page: Int = 1, per_page: Int = 50): StateMachineInstancePopulation!
      }

      type StateMachineInstancePopulation {
        count: Int!
        instances: [Model!]!
        page: Int!
        per_page: Int!
        has_more: Boolean!
      }

      type StateMachineState {
        name: String!
        label: String
        description: String
        is_final: Boolean!
      }

      type StateMachineTransition {
        name: String!
        from: StateMachineState!
        to: StateMachineState!
        label: String
        description: String
        action_json: String
        assignee_json: String
        requirements_json: String
        permission_json: String
        risk: String
        expected_outcome_json: String
      }

      type StateMachinePath {
        states: [StateMachineState!]!
        transitions: [StateMachineTransition!]!
      }

      type StateMachineTransitionEvent {
        sequence: Int!
        occurred_at: Float!
        transition: StateMachineTransition!
        from: StateMachineState!
        to: StateMachineState!
      }

      extend type Model {
        state(name: String!): StateMachineSnapshot
        states: [StateMachine!]!
        state_machine(name: String!): StateMachine
      }

      extend type ModelMutation {
        create_state_machine(name: String!, entry_state: String!): StateMachineMutation
        state(name: String!): StateMachineSnapshotMutation
        state_machine(name: String!): StateMachineMutation
      }
      `,
    ],
    createResolvers({ run, stateMachines }) {
      return {
        Model: {
          state: async (ant, { name }) => await run(ant.state_machine(name)),
          states: async (ant) => await run(ant.state_machines()),
          state_machine: async (ant, { name }) =>
            await run(ant.state_machine(name)),
        },
        ModelMutation: {
          create_state_machine: async (ant, { name, entry_state }) => {
            await run(ant.create_state_machine(name, entry_state));
            return { target: ant, name };
          },
          state: async (ant, { name }) => {
            const machine = await run(ant.state_machine(name));
            return machine ? { target: ant, name } : null;
          },
          state_machine: async (ant, { name }) => {
            const machine = await run(ant.state_machine(name));
            return machine ? { target: ant, name } : null;
          },
        },
        StateMachineMutation: {
          name: (value) => value.name,
          state_machine: async (value) =>
            await run(value.target.state_machine(value.name)),
          add_state: async (value, { name, is_final, label, description }) => {
            await run(
              value.target.add_state_machine_state(
                value.name,
                name,
                is_final ?? false,
                label,
                description,
              ),
            );
            return value;
          },
          add_transition: async (
            value,
            {
              name,
              from,
              to,
              label,
              description,
              action_json,
              assignee_json,
              requirements_json,
              permission_json,
              risk,
              expected_outcome_json,
            },
          ) => {
            await run(
              value.target.add_state_machine_transition(
                value.name,
                name,
                from,
                to,
                {
                  label,
                  description,
                  actionJson: action_json,
                  assigneeJson: assignee_json,
                  requirementsJson: requirements_json,
                  permissionJson: permission_json,
                  risk,
                  expectedOutcomeJson: expected_outcome_json,
                },
              ),
            );
            return value;
          },
          activate_transition: async (value, { name }) => {
            await run(
              value.target.activate_state_machine_transition(value.name, name),
            );
            return value;
          },
        },
        StateMachineSnapshotMutation: {
          snapshot: async (value) =>
            await run(value.target.state_machine(value.name)),
          activate_transition: async (value, { name }) => {
            await run(
              value.target.activate_state_machine_transition(value.name, name),
            );
            return value;
          },
          observe_state: async (value, { state, force, source }) => {
            await run(
              value.target.observe_state_machine_state(
                value.name,
                state,
                force === true,
                source,
              ),
            );
            return value;
          },
        },
        StateMachineState: {
          name: (value) => value.name,
          label: (value) => value.label || null,
          description: (value) => value.description || null,
          is_final: (value) => value.is_final,
        },
        StateMachineTransition: {
          name: (value) => value.name,
          from: (value) =>
            value.from_state || { name: value.from, is_final: false },
          to: (value) => value.to_state || { name: value.to, is_final: false },
          label: (value) => value.label || null,
          description: (value) => value.description || null,
          action_json: (value) => value.action_json || null,
          assignee_json: (value) => value.assignee_json || null,
          requirements_json: (value) => value.requirements_json || null,
          permission_json: (value) => value.permission_json || null,
          risk: (value) => value.risk || null,
          expected_outcome_json: (value) => value.expected_outcome_json || null,
        },
        StateMachinePath: {
          states: (value) => value.states,
          transitions: (value) => value.transitions,
        },
        StateMachineTransitionEvent: {
          sequence: (value) => value.sequence,
          occurred_at: (value) => value.occurred_at,
          transition: (value) => value.transition,
          from: (value) => value.from,
          to: (value) => value.to,
        },
        StateMachineSnapshot: {
          name: (value) => value.name,
          current_state: (value) => value.current_state,
          available_transitions: (value) => value.available_transitions,
          reachable_states: (value) => value.reachable_states,
          is_final: (value) => value.is_final,
          history: (value) => value.history,
          paths_to: async (value, { state }) =>
            await stateMachines!.pathsToState(
              value.model.target || value.model,
              value.name,
              state,
            ),
        },
        StateMachine: {
          name: (value) => value.name,
          model: (value) => value.model,
          entry_state: (value) => value.entry_state,
          states: (value) => value.states,
          transitions: (value) => value.transitions,
          current_state: (value) => value.current_state,
          available_transitions: (value) => value.available_transitions,
          reachable_states: (value) => value.reachable_states,
          is_final: (value) => value.is_final,
          history: (value) => value.history,
          paths_to: async (value, { state }) =>
            await stateMachines!.pathsToState(
              value.model.target || value.model,
              value.name,
              state,
            ),
          instances_in_state: async (value, { state }) =>
            await stateMachines!.instancesInState(
              value.model.target || value.model,
              value.name,
              state,
            ),
          instance_population: async (value, { state, page, per_page }) =>
            await stateMachines!.instancePopulation(
              value.model.target || value.model,
              value.name,
              state,
              page,
              per_page,
            ),
        },
      };
    },
  },
  manifest: {
    buildModelMutations(modelPath, spec) {
      return buildStateMachineModelMutations(modelPath, spec.stateMachines);
    },
  },
  summary: {
    selections: {
      classFields: [
        `states {
          name
          entry_state { name }
          states { name is_final }
          transitions {
            name
            from { name }
            to { name }
            label
            description
            action_json
            assignee_json
            requirements_json
            permission_json
            risk
            expected_outcome_json
          }
        }`,
      ],
    },
    readClassSummary: readStateMachineSummaries,
  },
  domain: {
    extraTypesForClass(classSummary) {
      return classSummary.stateMachines.flatMap((machine) =>
        buildMachineTypes(classSummary, machine),
      );
    },
    extraMethodsForClass(classSummary) {
      return classSummary.stateMachines.flatMap((machine) =>
        buildMachineMethods(classSummary, machine),
      );
    },
  },
});

export function renderUnionType(typeName: string, members: string[]) {
  if (members.length === 0) return `export type ${typeName} = never;`;
  return `export type ${typeName} = ${members.map(quoteTsStringLiteral).join(" | ")};`;
}
